import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth';
import LanguageSelector from './LanguageSelector';
import './Header.css';

const Header = ({ connectionStatus }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const user = authService.getUser();
  const isAuthenticated = authService.isAuthenticated();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    
    if (path === '/vote-counting') {
      return location.pathname === '/vote-counting' || location.pathname.startsWith('/vote-counting');
    }
    
    if (path === '/survey') {
      return location.pathname === '/survey';
    }
    
    if (path === '/survey/results') {
      return location.pathname === '/survey/results';
    }

    if (path === '/voter-certificate') {
      return location.pathname === '/voter-certificate' || location.pathname.startsWith('/voter-certificate');
    }

    if (path === '/poll-percentage') {
      return location.pathname === '/poll-percentage' || location.pathname.startsWith('/poll-percentage');
    }

    if (path === '/winner-runnerup') {
      return location.pathname === '/winner-runnerup' || location.pathname.startsWith('/winner-runnerup');
    }
    
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    if (window.confirm(t('auth.logout') + '?')) {
      authService.logout();
      setMobileMenuOpen(false);
      navigate('/login');
    }
  };

  const navItems = [
    { path: '/', label: t('header.home', 'Home'), icon: 'fa-home' },
    { path: '/vote-counting', label: t('header.voteCounting', 'Vote Counting'), icon: 'fa-chart-line' },
    { path: '/winners-runnerup', label: t('header.winnerRunnerUp', 'Winners & Runners-up'), icon: 'fa-trophy' },
    { path: '/nominations', label: t('results.allCandidates', 'All Candidates'), icon: 'fa-users' },
    { path: '/survey', label: t('header.voterPoll', 'Voter Poll'), icon: 'fa-poll' },
    { path: '/survey/results', label: t('header.pollResults', 'Poll Results'), icon: 'fa-chart-bar' },
    { path: '/poll-percentage', label: t('header.pollPercentage', 'Poll Percentage'), icon: 'fa-percentage' },
    { path: '/voter-certificate', label: t('header.voterCertificate', 'Voter Certificate'), icon: 'fa-id-card' },
    { path: '/news', label: t('header.news', 'News'), icon: 'fa-newspaper' },
    { path: '/blog', label: t('header.voterAwareness', 'Voter Awareness'), icon: 'fa-book-open' }
  ];

  return (
    <>
      <header className={`eci-header ${scrolled ? 'scrolled' : ''}`} role="banner">
        <div className="eci-topbar">
          <div className="topbar-container">
            <div className="topbar-left">
              <button 
                className="mobile-menu-trigger" 
                onClick={() => setMobileMenuOpen(true)}
                aria-label={t('common.menu') || 'Open menu'}
              >
                <i className="fas fa-bars"></i>
              </button>
              
              <div 
                className="site-branding" 
                onClick={() => navigate('/')}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate('/');
                  }
                }}
              >
                <img 
                  src="/logo192.png" 
                  alt={t('header.title') || 'Bihar Election Logo'}
                  className="site-logo"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div className="site-title">
                  <h1>{t('header.title') || 'Bihar Election 2025'}</h1>
                </div>
              </div>
            </div>

            <div className="topbar-right">
              <LanguageSelector />
              
              <div className="auth-section">
                {isAuthenticated ? (
                  <>
                    <button 
                      className="topbar-user-btn" 
                      onClick={() => navigate('/admin')}
                      title={t('header.adminDashboard') || 'Admin Dashboard'}
                    >
                      <i className="fas fa-user-circle"></i>
                      <span>{user?.username || t('header.admin') || 'Admin'}</span>
                    </button>
                    <button 
                      className="topbar-logout-btn" 
                      onClick={handleLogout} 
                      title={t('auth.logout') || 'Logout'}
                    >
                      <i className="fas fa-sign-out-alt"></i>
                    </button>
                  </>
                ) : (
                  <button 
                    className="topbar-login-btn" 
                    onClick={() => navigate('/login')}
                    title={t('auth.loginButton') || 'Login'}
                  >
                    <i className="fas fa-sign-in-alt"></i>
                    <span>{t('auth.loginButton') || 'Login'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <nav className="eci-navbar" role="navigation" aria-label={t('common.navigation') || 'Main navigation'}>
          <div className="navbar-container">
            <ul className="nav-menu">
              {navItems.map((item) => (
                <li key={item.path}>
                  <button
                    className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => navigate(item.path)}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                    title={item.label}
                  >
                    <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>

            {connectionStatus && (
              <div 
                className={`nav-status ${connectionStatus.connected ? 'connected' : 'disconnected'}`}
                role="status"
                aria-live="polite"
              >
                <i className="fas fa-circle" aria-hidden="true"></i>
                <span>
                  {connectionStatus.connected 
                    ? t('connection.connected') || 'Connected' 
                    : t('connection.disconnected') || 'Disconnected'
                  }
                </span>
              </div>
            )}
          </div>
        </nav>
      </header>

      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`} 
        onClick={() => setMobileMenuOpen(false)}
        role="presentation"
      >
        <div 
          className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`} 
          onClick={(e) => e.stopPropagation()}
          role="navigation"
          aria-label={t('common.navigation') || 'Mobile navigation'}
        >
          <div className="mobile-header">
            <div className="mobile-logo">
              <img 
                src="/logo192.png" 
                alt={t('header.title') || 'Logo'}
                className="mobile-logo-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span>{t('header.title') || 'Bihar Election 2025'}</span>
            </div>
            <button 
              className="mobile-close" 
              onClick={() => setMobileMenuOpen(false)}
              aria-label={t('common.close') || 'Close menu'}
              title={t('common.close') || 'Close'}
            >
              <i className="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <ul className="mobile-menu">
            {navItems.map((item) => (
              <li key={item.path}>
                <button
                  className={`mobile-menu-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                  <span>{item.label}</span>
                  {isActive(item.path) && (
                    <i className="fas fa-chevron-right check-icon" aria-hidden="true"></i>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="mobile-footer">
            {isAuthenticated ? (
              <>
                <button 
                  className="mobile-action-btn admin" 
                  onClick={() => {
                    navigate('/admin');
                    setMobileMenuOpen(false);
                  }}
                  title={t('header.adminDashboard') || 'Admin Dashboard'}
                >
                  <i className="fas fa-user-shield" aria-hidden="true"></i>
                  <span>{t('header.adminDashboard') || 'Admin Dashboard'}</span>
                </button>
                <button 
                  className="mobile-action-btn logout" 
                  onClick={handleLogout}
                  title={t('auth.logout') || 'Logout'}
                >
                  <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
                  <span>{t('auth.logout') || 'Logout'}</span>
                </button>
              </>
            ) : (
              <button 
                className="mobile-action-btn login" 
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                title={t('auth.loginButton') || 'Login'}
              >
                <i className="fas fa-sign-in-alt" aria-hidden="true"></i>
                <span>{t('auth.loginButton') || 'Login'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
